import React, { useState, useEffect } from 'react';
import { parseCurrency } from '@/shared/utils/currency';

export interface MoneyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  currency?: string;
  error?: string;
  label?: string;
  required?: boolean;
  hideHelperText?: boolean;
}

/**
 * MoneyInput - Input component for currency values
 *
 * Features:
 * - Brazilian Real formatting (R$ 1.234,56)
 * - Real-time formatting as user types
 * - Accepts various input formats
 * - Returns decimal number to backend
 * - Validation support
 *
 * @example
 * <MoneyInput
 *   label="Valor Estimado"
 *   value={budget}
 *   onChange={setBudget}
 *   placeholder="0,00"
 * />
 */
export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  ({
    value,
    onChange,
    currency = 'BRL',
    error,
    label,
    required,
    hideHelperText,
    className = '',
    placeholder = '0,00',
    disabled,
    ...props
  }, ref) => {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Initialize display value from prop value
    useEffect(() => {
      if (!isFocused && value !== undefined) {
        // Format the number with thousands separator and 2 decimals
        const formatted = new Intl.NumberFormat('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
        setDisplayValue(formatted);
      } else if (value === undefined && !isFocused) {
        setDisplayValue('');
      }
    }, [value, isFocused]);

    // Format currency in real-time as user types
    const formatCurrencyRealtime = (input: string): string => {
      // Remove all non-numeric characters
      const numbers = input.replace(/\D/g, '');

      if (!numbers) return '';

      // Convert to number and divide by 100 to get decimal places
      const numberValue = parseInt(numbers, 10) / 100;

      // Format with thousands separator and 2 decimals
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numberValue);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Allow empty value
      if (inputValue === '') {
        setDisplayValue('');
        onChange(undefined);
        return;
      }

      // Format in real-time
      const formatted = formatCurrencyRealtime(inputValue);
      setDisplayValue(formatted);

      // Parse to number for onChange callback
      const parsed = parseCurrency(formatted);
      onChange(parsed !== null ? parsed : undefined);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Borda/anel vivem no container segmentado (.money), não no input
      const wrap = e.currentTarget.parentElement;
      if (wrap && !error && !disabled) {
        wrap.style.borderColor = 'var(--theme-input-focus)';
        wrap.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.15)';
      }
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      const wrap = e.currentTarget.parentElement;
      if (wrap) {
        wrap.style.borderColor = error ? 'var(--theme-error-color)' : 'var(--theme-input-border)';
        wrap.style.boxShadow = 'none';
      }

      // Format on blur if there's a value
      if (displayValue && value !== undefined) {
        const formatted = new Intl.NumberFormat('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
        setDisplayValue(formatted);
      }

      props.onBlur?.(e);
    };

    return (
      <div className={`w-full ${className}`}>
        {label && (
          <label className="block text-[13px] font-semibold mb-1.5" style={{ color: 'var(--theme-text)' }}>
            {label}
            {required && <span style={{ color: 'var(--theme-error-color)' }} className="ml-1">*</span>}
          </label>
        )}

        {/* `.money` do mockup: segmento 'R$' com fundo sunken + divisor, input sem borda */}
        <div
          className="flex items-stretch h-[38px] rounded-[9px] border overflow-hidden"
          style={{
            backgroundColor: 'var(--theme-input-background)',
            borderColor: error ? 'var(--theme-error-color)' : 'var(--theme-input-border)',
            transition: 'border-color 150ms, box-shadow 150ms',
          }}
        >
          {/* `.cur`: prefixo R$ segmentado */}
          <div
            className="flex items-center px-[11px] text-sm font-semibold flex-none pointer-events-none"
            style={{
              // tom recuado theme-agnóstico (funciona em página clara/escura e dentro de drawer)
              backgroundColor: 'rgba(0,0,0,0.04)',
              borderRight: '1px solid var(--theme-input-border)',
              color: 'var(--theme-muted-text)',
            }}
          >
            R$
          </div>

          {/* Input (sem borda própria — vive dentro do container segmentado) */}
          <input
            ref={ref}
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 min-w-0 h-full px-3 py-2 text-sm bg-transparent border-0 focus-visible:outline-none"
            style={{ color: 'var(--theme-input-text)' }}
            {...props}
          />
        </div>

        {/* Error message */}
        {error && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>
            {error}
          </p>
        )}

        {/* Helper text */}
        {!error && !hideHelperText && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
            Digite apenas números (ex: 50000 ou 1234567)
          </p>
        )}
      </div>
    );
  }
);

MoneyInput.displayName = 'MoneyInput';
